import { ApplicationForUser } from '../models/borrower-portal/application-for-user-model';
import { LiveDataTotalModel } from '../models/borrower-portal/live-data-total-model';
import { LoanStatus } from '../models/loan-status.model';

/**
 * Recomputes the aggregate task total, excluding applications whose
 * loan status has hideFromCounter = true.  Message counts are preserved
 * from the original total (unaffected by hideFromCounter).
 *
 * Returns the original total unchanged when no apps need hiding.
 */
export function filterLiveDataTotal(
  loanStatuses: Array<Pick<LoanStatus, 'hideFromCounter' | 'loanStatusId'>>,
  applications: ApplicationForUser[],
  applicationTotals: Record<number, LiveDataTotalModel> | undefined,
  originalTotal: LiveDataTotalModel | undefined
): LiveDataTotalModel | undefined {
  if (!applicationTotals || !loanStatuses?.length) {
    return originalTotal;
  }

  const hideFromCounterStatusIds = new Set(
    loanStatuses
      .filter(ls => ls.hideFromCounter)
      .map(ls => ls.loanStatusId)
  );

  if (hideFromCounterStatusIds.size === 0) {
    return originalTotal;
  }

  const hiddenAppIds = new Set(
    applications
      .filter(app => hideFromCounterStatusIds.has(app.loanStatusId))
      .map(app => app.applicationId)
  );

  if (hiddenAppIds.size === 0) {
    return originalTotal;
  }

  const filteredTotal = new LiveDataTotalModel();
  filteredTotal.taskCount = 0;
  filteredTotal.messageCount = originalTotal?.messageCount || 0;

  for (const appIdStr of Object.keys(applicationTotals)) {
    const appId = Number(appIdStr);
    if (!hiddenAppIds.has(appId)) {
      filteredTotal.taskCount += applicationTotals[appId]?.taskCount || 0;
    }
  }

  return filteredTotal;
}

/**
 * Zeros out taskCount in applicationTotals for apps whose loan status
 * has hideFromCounter = true.  Mutates the passed record in-place.
 */
export function zeroHiddenAppTotals(
  loanStatuses: Array<Pick<LoanStatus, 'hideFromCounter' | 'loanStatusId'>>,
  applications: ApplicationForUser[],
  applicationTotals: Record<number, LiveDataTotalModel>
): void {
  if (!loanStatuses?.length) {
    return;
  }

  const hideFromCounterStatusIds = new Set(
    loanStatuses
      .filter(ls => ls.hideFromCounter)
      .map(ls => ls.loanStatusId)
  );

  if (hideFromCounterStatusIds.size === 0) {
    return;
  }

  for (const app of applications) {
    if (hideFromCounterStatusIds.has(app.loanStatusId) && applicationTotals[app.applicationId]) {
      applicationTotals[app.applicationId] = {
        ...applicationTotals[app.applicationId],
        taskCount: 0
      };
    }
  }
}
